import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Slider } from '../components/ui/slider';
import { Badge } from '../components/ui/badge';
import { 
  ArrowLeft, Play, Pause, SkipBack, SkipForward, 
  Pencil, Circle, ArrowRight, Type, Mic, MicOff,
  Scissors, Save, Send, Trash2, Loader2, MousePointer, 
  Minus, Info, ChevronDown, ChevronUp, X, Volume2, VolumeX, Volume1, Eye
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Drawing tools
const TOOLS = {
  SELECT: 'select',
  LINE: 'line',
  ARROW: 'arrow',
  CIRCLE: 'circle',
  FREEHAND: 'freehand',
  TEXT: 'text'
};

const TOOL_CONFIG = {
  select: { label: 'Select', icon: MousePointer, tip: 'Click annotations to select' },
  line: { label: 'Line', icon: Minus, tip: 'Drag to draw a line' },
  arrow: { label: 'Arrow', icon: ArrowRight, tip: 'Drag to draw an arrow' },
  circle: { label: 'Circle', icon: Circle, tip: 'Drag from center outward' },
  freehand: { label: 'Draw', icon: Pencil, tip: 'Draw freely on video' },
  text: { label: 'Text', icon: Type, tip: 'Tap where you want text' }
};

// Colors for annotation
const COLORS = ['#ef4444', '#f97316', '#eab308', '#22c55e', '#3b82f6', '#8b5cf6', '#ffffff'];

const VideoStudioPage = () => {
  const { projectId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  // Project state
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [showPublishConfirm, setShowPublishConfirm] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  
  // Video state
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [videoReady, setVideoReady] = useState(false);
  const [videoError, setVideoError] = useState(null);
  const [videoVolume, setVideoVolume] = useState(0.3); // Lower default for video
  const [voiceOverVolume, setVoiceOverVolume] = useState(1); // Full volume for voiceover
  const [isMuted, setIsMuted] = useState(false);
  const [showVolumeSlider, setShowVolumeSlider] = useState(false);
  
  // Drawing state
  const [activeTool, setActiveTool] = useState(TOOLS.FREEHAND);
  const [activeColor, setActiveColor] = useState('#ef4444');
  const [lineWidth, setLineWidth] = useState(4);
  const [annotations, setAnnotations] = useState([]);
  const [currentAnnotation, setCurrentAnnotation] = useState(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const initialAnnotationsRef = useRef([]);
  
  // Audio recording state
  const [isRecording, setIsRecording] = useState(false);
  const [voiceOverUrl, setVoiceOverUrl] = useState(null);
  const [recordingStartTime, setRecordingStartTime] = useState(0);
  const mediaRecorderRef = useRef(null);
  const voiceOverAudioRef = useRef(null);
  
  // Trim state
  const [trimStart, setTrimStart] = useState(0);
  const [trimEnd, setTrimEnd] = useState(0);
  const [showTrimControls, setShowTrimControls] = useState(false);
  const [isDraggingTrim, setIsDraggingTrim] = useState(null);
  
  // Text input state
  const [textInput, setTextInput] = useState('');
  const [textPosition, setTextPosition] = useState(null);
  
  // Mobile UI state
  const [showToolsPanel, setShowToolsPanel] = useState(false);
  const [activePanel, setActivePanel] = useState('draw'); // 'draw', 'voice', 'trim'

  // Load project or create new
  useEffect(() => {
    const loadOrCreate = async () => {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }
      
      try {
        if (projectId && projectId !== 'new') {
          // Load existing project by ID
          const response = await axios.get(`${API_URL}/api/video-studio/projects/${projectId}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setProject(response.data);
          const loadedAnnotations = response.data.annotations || [];
          setAnnotations(loadedAnnotations);
          initialAnnotationsRef.current = loadedAnnotations;
          setVoiceOverUrl(response.data.voice_over_url);
          if (response.data.trim_start) setTrimStart(response.data.trim_start);
          if (response.data.trim_end) setTrimEnd(response.data.trim_end);
          if (response.data.voice_over_start_time !== undefined) setRecordingStartTime(response.data.voice_over_start_time);
        } else {
          const sourceVideo = searchParams.get('video');
          const critiqueId = searchParams.get('critique');
          const studentId = searchParams.get('student');
          
          if (sourceVideo && critiqueId) {
            // Check if a project already exists for this critique
            const existingProjects = await axios.get(`${API_URL}/api/video-studio/projects`, {
              headers: { Authorization: `Bearer ${token}` }
            });
            
            const existingProject = existingProjects.data.projects?.find(
              p => p.critique_id === critiqueId
            );
            
            if (existingProject) {
              // Use existing draft project
              setProject(existingProject);
              const loadedAnnotations = existingProject.annotations || [];
              setAnnotations(loadedAnnotations);
              initialAnnotationsRef.current = loadedAnnotations;
              setVoiceOverUrl(existingProject.voice_over_url);
              if (existingProject.trim_start) setTrimStart(existingProject.trim_start);
              if (existingProject.trim_end) setTrimEnd(existingProject.trim_end);
              if (existingProject.voice_over_start_time !== undefined) setRecordingStartTime(existingProject.voice_over_start_time);
              if (existingProject.trim_end) setTrimEnd(existingProject.trim_end);
              navigate(`/video-studio/${existingProject.id}`, { replace: true });
              toast.info('Loaded existing draft project');
            } else {
              // Create new project
              const response = await axios.post(`${API_URL}/api/video-studio/projects`, {
                title: 'New Training Video',
                source_video_url: sourceVideo,
                critique_id: critiqueId,
                student_id: studentId || null
              }, {
                headers: { Authorization: `Bearer ${token}` }
              });
              setProject(response.data);
              navigate(`/video-studio/${response.data.id}`, { replace: true });
            }
          } else if (sourceVideo) {
            // No critique, just create standalone project
            const response = await axios.post(`${API_URL}/api/video-studio/projects`, {
              title: 'New Training Video',
              source_video_url: sourceVideo,
              critique_id: null,
              student_id: null
            }, {
              headers: { Authorization: `Bearer ${token}` }
            });
            setProject(response.data);
            navigate(`/video-studio/${response.data.id}`, { replace: true });
          }
        }
      } catch (error) {
        console.error('Failed to load/create project:', error);
        toast.error('Failed to load project');
      } finally {
        setLoading(false);
      }
    };
    
    loadOrCreate();
  }, [projectId, searchParams, navigate]);

  // Track unsaved changes
  useEffect(() => {
    if (!loading && project) {
      const hasChanges = annotations.length !== initialAnnotationsRef.current.length ||
        JSON.stringify(annotations) !== JSON.stringify(initialAnnotationsRef.current);
      setHasUnsavedChanges(hasChanges);
    }
  }, [annotations, loading, project]);

  // Warn before leaving with unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  // Handle video metadata load
  const handleVideoLoad = () => {
    if (videoRef.current) {
      const videoDuration = videoRef.current.duration;
      setDuration(videoDuration);
      setVideoReady(true);
      setVideoError(null);
      if (trimEnd === 0) {
        setTrimEnd(videoDuration);
      }
      // Set initial video volume
      videoRef.current.volume = videoVolume;
    }
  };

  // Handle video error
  const handleVideoError = (e) => {
    console.error('Video error:', e);
    const url = project?.source_video_url || '';
    if (url.includes('youtube.com') || url.includes('youtu.be') || url.includes('vimeo.com')) {
      setVideoError('YouTube and Vimeo links cannot be annotated in Video Studio. The student needs to share a direct video file (MP4, MOV, WebM) for Video Studio to work.');
    } else {
      setVideoError('Unable to load video. The video URL may be invalid, expired, or the format is not supported by your browser.');
    }
    setVideoReady(false);
  };

  // Set voiceover volume when it changes or loads
  useEffect(() => {
    if (voiceOverAudioRef.current) {
      voiceOverAudioRef.current.volume = voiceOverVolume;
    }
  }, [voiceOverVolume, voiceOverUrl]);

  // Update canvas size when video size changes
  useEffect(() => {
    const updateCanvasSize = () => {
      if (videoRef.current && canvasRef.current) {
        const rect = videoRef.current.getBoundingClientRect();
        canvasRef.current.width = rect.width;
        canvasRef.current.height = rect.height;
      }
    };
    
    updateCanvasSize();
    window.addEventListener('resize', updateCanvasSize);
    return () => window.removeEventListener('resize', updateCanvasSize);
  }, [project, videoReady]);

  // Update video time
  const handleTimeUpdate = () => {
    if (videoRef.current) {
      setCurrentTime(videoRef.current.currentTime);
    }
  };

  // Play/Pause toggle - syncs video and voice-over
  const togglePlay = useCallback(() => {
    if (videoRef.current && videoReady) {
      if (isPlaying) {
        videoRef.current.pause();
        // Pause voice-over if playing
        if (voiceOverAudioRef.current) {
          voiceOverAudioRef.current.pause();
        }
      } else {
        videoRef.current.play().catch(err => {
          console.error('Play error:', err);
          toast.error('Unable to play video');
        });
        // Play voice-over in sync if available and we're past the recording start point
        if (voiceOverAudioRef.current && voiceOverUrl) {
          const voiceOverStartTime = recordingStartTime || 0;
          const videoTime = videoRef.current.currentTime;
          if (videoTime >= voiceOverStartTime) {
            voiceOverAudioRef.current.currentTime = videoTime - voiceOverStartTime;
            voiceOverAudioRef.current.play().catch(() => {});
          }
        }
      }
    }
  }, [isPlaying, videoReady, voiceOverUrl, recordingStartTime]);

  // Seek video - also seek voice-over
  const seekTo = useCallback((time) => {
    if (videoRef.current) {
      videoRef.current.currentTime = time;
      setCurrentTime(time);
      // Sync voice-over position
      if (voiceOverAudioRef.current && voiceOverUrl) {
        const voiceOverStartTime = recordingStartTime || 0;
        if (time >= voiceOverStartTime) {
          voiceOverAudioRef.current.currentTime = time - voiceOverStartTime;
        } else {
          voiceOverAudioRef.current.currentTime = 0;
          voiceOverAudioRef.current.pause();
        }
      }
    }
  }, [voiceOverUrl, recordingStartTime]);

  // Toggle mute for video
  const toggleMute = useCallback(() => {
    if (videoRef.current) {
      if (isMuted) {
        videoRef.current.muted = false;
        videoRef.current.volume = videoVolume;
        setIsMuted(false);
      } else {
        videoRef.current.muted = true;
        setIsMuted(true);
      }
    }
  }, [isMuted, videoVolume]);

  // Change video volume
  const handleVideoVolumeChange = useCallback((newVolume) => {
    if (videoRef.current) {
      const vol = newVolume[0];
      videoRef.current.volume = vol;
      setVideoVolume(vol);
      if (vol === 0) {
        setIsMuted(true);
        videoRef.current.muted = true;
      } else if (isMuted) {
        setIsMuted(false);
        videoRef.current.muted = false;
      }
    }
  }, [isMuted]);

  // Change voiceover volume
  const handleVoiceOverVolumeChange = useCallback((newVolume) => {
    const vol = newVolume[0];
    setVoiceOverVolume(vol);
    if (voiceOverAudioRef.current) {
      voiceOverAudioRef.current.volume = vol;
    }
  }, []);

  // Format time display
  const formatTime = (seconds) => {
    if (!seconds || isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Get canvas coordinates from event (works for both mouse and touch)
  const getCanvasCoords = (e) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    
    const rect = canvas.getBoundingClientRect();
    
    if (e.touches && e.touches.length > 0) {
      return {
        x: e.touches[0].clientX - rect.left,
        y: e.touches[0].clientY - rect.top
      };
    }
    
    return {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    };
  };

  // Start drawing
  const handleDrawStart = (e) => {
    e.preventDefault();
    if (activeTool === TOOLS.SELECT) return;
    
    const coords = getCanvasCoords(e);
    setIsDrawing(true);
    
    if (activeTool === TOOLS.TEXT) {
      setTextPosition(coords);
      return;
    }
    
    setCurrentAnnotation({
      id: Date.now().toString(),
      type: activeTool,
      timestamp: currentTime,
      duration: 3,
      color: activeColor,
      lineWidth: lineWidth,
      points: [coords]
    });
  };

  // Continue drawing
  const handleDrawMove = (e) => {
    e.preventDefault();
    if (!isDrawing || activeTool === TOOLS.SELECT || activeTool === TOOLS.TEXT) return;
    
    const coords = getCanvasCoords(e);
    
    if (currentAnnotation) {
      setCurrentAnnotation(prev => ({
        ...prev,
        points: [...prev.points, coords]
      }));
    }
  };

  // End drawing
  const handleDrawEnd = (e) => {
    if (e) e.preventDefault();
    if (!isDrawing) return;
    setIsDrawing(false);
    
    if (currentAnnotation && currentAnnotation.points && currentAnnotation.points.length > 1) {
      setAnnotations(prev => [...prev, currentAnnotation]);
      toast.success(`${TOOL_CONFIG[activeTool]?.label || 'Annotation'} added`);
    }
    
    setCurrentAnnotation(null);
  };

  // Add text annotation
  const handleAddText = () => {
    if (!textInput.trim() || !textPosition) return;
    
    const textAnnotation = {
      id: Date.now().toString(),
      type: TOOLS.TEXT,
      timestamp: currentTime,
      duration: 3,
      color: activeColor,
      text: textInput,
      position: textPosition
    };
    
    setAnnotations(prev => [...prev, textAnnotation]);
    toast.success('Text added');
    setTextInput('');
    setTextPosition(null);
  };

  // Delete annotation
  const deleteAnnotation = (id) => {
    setAnnotations(prev => prev.filter(a => a.id !== id));
    toast.success('Annotation removed');
  };

  // Clear all annotations
  const clearAllAnnotations = () => {
    if (window.confirm('Clear all annotations?')) {
      setAnnotations([]);
      toast.success('All annotations cleared');
    }
  };

  // Draw on canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    
    // Draw annotations visible at current time
    const visibleAnnotations = annotations.filter(a => 
      currentTime >= a.timestamp && currentTime <= a.timestamp + a.duration
    );
    
    const allToDraw = currentAnnotation 
      ? [...visibleAnnotations, currentAnnotation]
      : visibleAnnotations;
    
    allToDraw.forEach(annotation => {
      ctx.strokeStyle = annotation.color;
      ctx.fillStyle = annotation.color;
      ctx.lineWidth = annotation.lineWidth || 4;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      
      const points = annotation.points || [];
      
      switch (annotation.type) {
        case TOOLS.LINE:
          if (points.length >= 2) {
            ctx.beginPath();
            ctx.moveTo(points[0].x, points[0].y);
            ctx.lineTo(points[points.length - 1].x, points[points.length - 1].y);
            ctx.stroke();
          }
          break;
          
        case TOOLS.ARROW:
          if (points.length >= 2) {
            const start = points[0];
            const end = points[points.length - 1];
            
            ctx.beginPath();
            ctx.moveTo(start.x, start.y);
            ctx.lineTo(end.x, end.y);
            ctx.stroke();
            
            const angle = Math.atan2(end.y - start.y, end.x - start.x);
            const headLength = 15;
            ctx.beginPath();
            ctx.moveTo(end.x, end.y);
            ctx.lineTo(
              end.x - headLength * Math.cos(angle - Math.PI / 6),
              end.y - headLength * Math.sin(angle - Math.PI / 6)
            );
            ctx.moveTo(end.x, end.y);
            ctx.lineTo(
              end.x - headLength * Math.cos(angle + Math.PI / 6),
              end.y - headLength * Math.sin(angle + Math.PI / 6)
            );
            ctx.stroke();
          }
          break;
          
        case TOOLS.CIRCLE:
          if (points.length >= 2) {
            const start = points[0];
            const end = points[points.length - 1];
            const radius = Math.sqrt(
              Math.pow(end.x - start.x, 2) + Math.pow(end.y - start.y, 2)
            );
            ctx.beginPath();
            ctx.arc(start.x, start.y, radius, 0, 2 * Math.PI);
            ctx.stroke();
          }
          break;
          
        case TOOLS.FREEHAND:
          if (points.length >= 2) {
            ctx.beginPath();
            ctx.moveTo(points[0].x, points[0].y);
            points.forEach(point => {
              ctx.lineTo(point.x, point.y);
            });
            ctx.stroke();
          }
          break;
          
        case TOOLS.TEXT:
          if (annotation.text && annotation.position) {
            ctx.font = 'bold 20px Inter, sans-serif';
            ctx.fillText(annotation.text, annotation.position.x, annotation.position.y);
          }
          break;
          
        default:
          break;
      }
    });
  }, [annotations, currentAnnotation, currentTime]);

  // Start voice recording
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      
      const chunks = [];
      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunks.push(e.data);
        }
      };
      
      mediaRecorder.onstop = () => {
        const blob = new Blob(chunks, { type: 'audio/webm' });
        uploadVoiceOver(blob);
        stream.getTracks().forEach(track => track.stop());
      };
      
      // Store the video timestamp when recording starts
      const startTime = videoRef.current ? videoRef.current.currentTime : 0;
      setRecordingStartTime(startTime);
      
      mediaRecorder.start();
      setIsRecording(true);
      toast.success('Recording started - speak while watching the video');
      
      // Start playing video if not already
      if (videoRef.current && !isPlaying) {
        videoRef.current.play();
        setIsPlaying(true);
      }
    } catch (error) {
      console.error('Failed to start recording:', error);
      toast.error('Could not access microphone');
    }
  };

  // Stop voice recording
  const stopRecording = () => {
    if (mediaRecorderRef.current) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      toast.success('Recording saved! It will play in sync with the video.');
      
      if (videoRef.current) {
        videoRef.current.pause();
        setIsPlaying(false);
      }
    }
  };

  // Upload voice over
  const uploadVoiceOver = async (blob) => {
    if (!project) return;
    
    const token = localStorage.getItem('token');
    const formData = new FormData();
    formData.append('file', blob, 'voiceover.webm');
    
    try {
      // First upload the voice-over file
      const response = await axios.post(
        `${API_URL}/api/video-studio/projects/${project.id}/voice-over`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      setVoiceOverUrl(response.data.voice_over_url);
      
      // Also save the voice_over_start_time immediately so it's synced
      await axios.put(`${API_URL}/api/video-studio/projects/${project.id}`, {
        voice_over_start_time: recordingStartTime || 0
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Voice-over saved and synced!');
    } catch (error) {
      console.error('Failed to upload voice-over:', error);
      toast.error('Failed to save voice-over');
    }
  };

  // Save project
  const saveProject = async () => {
    if (!project) return;
    
    setSaving(true);
    const token = localStorage.getItem('token');
    
    try {
      const saveData = {
        annotations: annotations,
        trim_start: showTrimControls ? trimStart : null,
        trim_end: showTrimControls ? trimEnd : null,
        voice_over_start_time: recordingStartTime || 0
      };
      
      console.log('Saving project with data:', saveData);
      
      await axios.put(`${API_URL}/api/video-studio/projects/${project.id}`, saveData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Update initial state to mark as saved
      initialAnnotationsRef.current = [...annotations];
      setHasUnsavedChanges(false);
      
      toast.success(`Saved! ${annotations.length} annotation${annotations.length !== 1 ? 's' : ''} stored.`);
      return true; // Return success
    } catch (error) {
      console.error('Failed to save project:', error);
      toast.error('Failed to save: ' + (error.response?.data?.detail || error.message));
      return false; // Return failure
    } finally {
      setSaving(false);
    }
  };

  // Save and Exit - saves then navigates back to critique or dashboard
  const saveAndExit = async () => {
    const success = await saveProject();
    if (success) {
      // Navigate back to the critique if we have one, otherwise dashboard
      if (project?.critique_id) {
        navigate(`/coach/dashboard?highlight=${project.critique_id}`);
      } else {
        navigate('/coach/dashboard');
      }
    }
  };

  // Publish video
  const publishVideo = async () => {
    if (!project) return;
    
    setPublishing(true);
    await saveProject();
    
    const token = localStorage.getItem('token');
    
    try {
      await axios.post(`${API_URL}/api/video-studio/projects/${project.id}/publish`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Video published!');
      navigate('/coach/dashboard');
    } catch (error) {
      console.error('Failed to publish:', error);
      toast.error('Failed to publish');
    } finally {
      setPublishing(false);
    }
  };

  // Trim bar handlers for drag interaction
  const handleTrimBarMouseDown = (e, handle) => {
    e.preventDefault();
    setIsDraggingTrim(handle);
  };

  const handleTrimBarMouseMove = useCallback((e) => {
    if (!isDraggingTrim || !duration) return;
    
    const bar = e.currentTarget;
    const rect = bar.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percentage = Math.max(0, Math.min(1, x / rect.width));
    const time = percentage * duration;
    
    if (isDraggingTrim === 'start') {
      setTrimStart(Math.min(time, trimEnd - 0.5));
      seekTo(Math.min(time, trimEnd - 0.5));
    } else if (isDraggingTrim === 'end') {
      setTrimEnd(Math.max(time, trimStart + 0.5));
      seekTo(Math.max(time, trimStart + 0.5));
    }
  }, [isDraggingTrim, duration, trimEnd, trimStart, seekTo]);

  const handleTrimBarMouseUp = useCallback(() => {
    setIsDraggingTrim(null);
  }, []);

  useEffect(() => {
    if (isDraggingTrim) {
      window.addEventListener('mouseup', handleTrimBarMouseUp);
      return () => window.removeEventListener('mouseup', handleTrimBarMouseUp);
    }
  }, [isDraggingTrim, handleTrimBarMouseUp]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!project) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-6 text-center">
            <p className="text-muted-foreground mb-4">No video project found. Please select a video to annotate.</p>
            <Button onClick={() => navigate('/coach/dashboard')}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Dashboard
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col overflow-x-hidden" data-testid="video-studio-page">
      {/* Header - Compact & Mobile Friendly */}
      <header className="border-b bg-card sticky top-0 z-20">
        <div className="px-3 py-2 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={() => {
                if (hasUnsavedChanges) {
                  if (window.confirm('You have unsaved changes. Are you sure you want to leave?')) {
                    navigate('/coach/dashboard');
                  }
                } else {
                  navigate('/coach/dashboard');
                }
              }}
              className="h-8 w-8 flex-shrink-0"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <div className="flex items-center gap-2 min-w-0">
              <h1 className="font-semibold text-sm sm:text-base truncate">Video Studio</h1>
              <Badge variant="secondary" className="text-xs hidden sm:inline-flex">
                {project.status}
              </Badge>
            </div>
          </div>
          
          <div className="flex items-center gap-1 sm:gap-2">
            <Button 
              variant="outline"
              size="sm" 
              onClick={saveAndExit} 
              disabled={saving}
              className="h-8 px-2 sm:px-3"
              data-testid="save-exit-btn"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              <span className="hidden sm:inline ml-1">Save & Exit</span>
            </Button>
            <Button 
              variant={hasUnsavedChanges ? "default" : "outline"}
              size="sm" 
              onClick={saveProject} 
              disabled={saving}
              className={`h-8 px-2 sm:px-3 ${hasUnsavedChanges ? 'animate-pulse' : ''}`}
              data-testid="save-btn"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              <span className="hidden sm:inline ml-1">{hasUnsavedChanges ? 'Save*' : 'Save'}</span>
            </Button>
            <Button 
              variant="outline"
              size="sm" 
              onClick={() => setShowPreview(true)} 
              className="h-8 px-2 sm:px-3"
              data-testid="preview-btn"
            >
              <Eye className="w-4 h-4" />
              <span className="hidden sm:inline ml-1">Preview</span>
            </Button>
            <Button 
              size="sm" 
              onClick={() => setShowPublishConfirm(true)} 
              disabled={publishing}
              className="h-8 px-2 sm:px-3"
              data-testid="publish-btn"
            >
              {publishing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              <span className="hidden sm:inline ml-1">Publish</span>
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="flex-1 flex flex-col lg:flex-row overflow-hidden">
        {/* Video Section */}
        <div className="flex-1 flex flex-col min-w-0 p-2 sm:p-4">
          {/* Video Container */}
          <div 
            ref={containerRef}
            className="relative bg-black rounded-lg overflow-hidden flex-shrink-0"
            style={{ aspectRatio: '16/9', maxHeight: 'calc(100vh - 280px)' }}
          >
            {videoError ? (
              <div className="absolute inset-0 flex items-center justify-center text-white text-center p-4">
                <div>
                  <Info className="w-10 h-10 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">{videoError}</p>
                </div>
              </div>
            ) : (
              <>
                <video
                  ref={videoRef}
                  src={project.source_video_url}
                  className="w-full h-full object-contain"
                  onLoadedMetadata={handleVideoLoad}
                  onTimeUpdate={handleTimeUpdate}
                  onPlay={() => setIsPlaying(true)}
                  onPause={() => {
                    setIsPlaying(false);
                    // Also pause voice-over when video pauses
                    if (voiceOverAudioRef.current) {
                      voiceOverAudioRef.current.pause();
                    }
                  }}
                  onError={handleVideoError}
                  playsInline
                  data-testid="video-player"
                />
                
                {/* Hidden audio element for voice-over sync playback */}
                {voiceOverUrl && (
                  <audio
                    ref={voiceOverAudioRef}
                    src={voiceOverUrl}
                    preload="auto"
                    style={{ display: 'none' }}
                  />
                )}
                
                {/* Canvas Overlay */}
                <canvas
                  ref={canvasRef}
                  className="absolute inset-0 w-full h-full"
                  onMouseDown={handleDrawStart}
                  onMouseMove={handleDrawMove}
                  onMouseUp={handleDrawEnd}
                  onMouseLeave={handleDrawEnd}
                  onTouchStart={handleDrawStart}
                  onTouchMove={handleDrawMove}
                  onTouchEnd={handleDrawEnd}
                  style={{ 
                    cursor: activeTool === TOOLS.SELECT ? 'default' : 'crosshair',
                    touchAction: 'none'
                  }}
                  data-testid="drawing-canvas"
                />
                
                {/* Current Tool Indicator */}
                {activeTool !== TOOLS.SELECT && (
                  <div className="absolute top-2 left-2 bg-black/70 text-white px-2 py-1 rounded text-xs flex items-center gap-1.5">
                    <div className="w-2 h-2 rounded-full" style={{ backgroundColor: activeColor }} />
                    {TOOL_CONFIG[activeTool]?.label}
                  </div>
                )}
                
                {/* Recording indicator */}
                {isRecording && (
                  <div className="absolute top-2 right-2 flex items-center gap-1.5 bg-red-600 text-white px-2 py-1 rounded text-xs animate-pulse">
                    <div className="w-2 h-2 bg-white rounded-full" />
                    REC
                  </div>
                )}
              </>
            )}
          </div>

          {/* Video Controls */}
          <div className="mt-3 space-y-2">
            {/* Time & Progress */}
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground w-10 text-right font-mono">{formatTime(currentTime)}</span>
              <div className="flex-1 relative">
                <Slider
                  value={[currentTime]}
                  max={duration || 100}
                  step={0.1}
                  onValueChange={([value]) => seekTo(value)}
                  className="w-full"
                  data-testid="video-progress"
                />
                {/* Trim indicators on progress bar */}
                {showTrimControls && duration > 0 && (
                  <>
                    <div 
                      className="absolute top-1/2 -translate-y-1/2 w-0.5 h-4 bg-green-500 pointer-events-none"
                      style={{ left: `${(trimStart / duration) * 100}%` }}
                    />
                    <div 
                      className="absolute top-1/2 -translate-y-1/2 w-0.5 h-4 bg-red-500 pointer-events-none"
                      style={{ left: `${(trimEnd / duration) * 100}%` }}
                    />
                  </>
                )}
              </div>
              <span className="text-xs text-muted-foreground w-10 font-mono">{formatTime(duration)}</span>
            </div>
            
            {/* Playback Controls */}
            <div className="flex items-center justify-center gap-1">
              <Button 
                variant="ghost" 
                size="icon"
                onClick={() => seekTo(Math.max(0, currentTime - 5))}
                className="h-9 w-9"
                data-testid="skip-back-btn"
              >
                <SkipBack className="w-4 h-4" />
              </Button>
              <Button 
                onClick={togglePlay} 
                disabled={!videoReady}
                size="icon"
                className="h-12 w-12 rounded-full"
                data-testid="play-pause-btn"
              >
                {isPlaying ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5 ml-0.5" />}
              </Button>
              <Button 
                variant="ghost" 
                size="icon"
                onClick={() => seekTo(Math.min(duration, currentTime + 5))}
                className="h-9 w-9"
                data-testid="skip-forward-btn"
              >
                <SkipForward className="w-4 h-4" />
              </Button>
              
              {/* Volume Controls */}
              <div 
                className="relative ml-2 flex items-center"
                onMouseEnter={() => setShowVolumeSlider(true)}
                onMouseLeave={() => setShowVolumeSlider(false)}
              >
                <Button 
                  variant="ghost" 
                  size="icon"
                  onClick={toggleMute}
                  className="h-9 w-9"
                  data-testid="volume-btn"
                >
                  {isMuted || videoVolume === 0 ? (
                    <VolumeX className="w-4 h-4" />
                  ) : videoVolume < 0.5 ? (
                    <Volume1 className="w-4 h-4" />
                  ) : (
                    <Volume2 className="w-4 h-4" />
                  )}
                </Button>
                
                {/* Volume Sliders - shows on hover */}
                <div className={`absolute left-full ml-1 flex flex-col gap-2 bg-card border rounded-lg px-3 py-2 shadow-lg transition-opacity z-10 ${showVolumeSlider ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}>
                  {/* Video Volume */}
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-muted-foreground w-14">Video:</span>
                    <Slider
                      value={[isMuted ? 0 : videoVolume]}
                      max={1}
                      step={0.05}
                      onValueChange={handleVideoVolumeChange}
                      className="w-20"
                      data-testid="video-volume-slider"
                    />
                    <span className="text-xs text-muted-foreground w-7">
                      {Math.round((isMuted ? 0 : videoVolume) * 100)}%
                    </span>
                  </div>
                  {/* Voiceover Volume */}
                  {voiceOverUrl && (
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-muted-foreground w-14">Voice:</span>
                      <Slider
                        value={[voiceOverVolume]}
                        max={1}
                        step={0.05}
                        onValueChange={handleVoiceOverVolumeChange}
                        className="w-20"
                        data-testid="voiceover-volume-slider"
                      />
                      <span className="text-xs text-muted-foreground w-7">
                        {Math.round(voiceOverVolume * 100)}%
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Trim Controls (when active) */}
          {showTrimControls && (
            <div className="mt-3 p-3 bg-muted/50 rounded-lg space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">Trim Range</span>
                <span className="text-xs text-muted-foreground font-mono">
                  {formatTime(trimStart)} - {formatTime(trimEnd)}
                </span>
              </div>
              
              {/* Visual Trim Bar */}
              <div 
                className="relative h-10 bg-muted rounded cursor-pointer"
                onMouseMove={handleTrimBarMouseMove}
                data-testid="trim-bar"
              >
                {/* Selected range */}
                <div 
                  className="absolute h-full bg-primary/30 rounded"
                  style={{
                    left: `${(trimStart / (duration || 1)) * 100}%`,
                    width: `${((trimEnd - trimStart) / (duration || 1)) * 100}%`
                  }}
                />
                
                {/* Start handle */}
                <div 
                  className="absolute top-0 bottom-0 w-3 bg-green-500 rounded-l cursor-ew-resize flex items-center justify-center hover:bg-green-400 transition-colors"
                  style={{ left: `calc(${(trimStart / (duration || 1)) * 100}% - 6px)` }}
                  onMouseDown={(e) => handleTrimBarMouseDown(e, 'start')}
                  data-testid="trim-start-handle"
                >
                  <div className="w-0.5 h-4 bg-white/50 rounded" />
                </div>
                
                {/* End handle */}
                <div 
                  className="absolute top-0 bottom-0 w-3 bg-red-500 rounded-r cursor-ew-resize flex items-center justify-center hover:bg-red-400 transition-colors"
                  style={{ left: `calc(${(trimEnd / (duration || 1)) * 100}% - 6px)` }}
                  onMouseDown={(e) => handleTrimBarMouseDown(e, 'end')}
                  data-testid="trim-end-handle"
                >
                  <div className="w-0.5 h-4 bg-white/50 rounded" />
                </div>
                
                {/* Playhead */}
                <div 
                  className="absolute top-0 bottom-0 w-0.5 bg-white shadow-lg pointer-events-none"
                  style={{ left: `${(currentTime / (duration || 1)) * 100}%` }}
                />
              </div>
              
              {/* Preview buttons */}
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => seekTo(trimStart)}
                  className="flex-1 text-xs"
                >
                  Go to Start
                </Button>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => seekTo(Math.max(0, trimEnd - 2))}
                  className="flex-1 text-xs"
                >
                  Go to End
                </Button>
              </div>
            </div>
          )}

          {/* Annotations List - Compact */}
          {annotations.length > 0 && (
            <div className="mt-3 p-2 bg-muted/30 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-medium">Annotations ({annotations.length})</span>
                <Button variant="ghost" size="sm" onClick={clearAllAnnotations} className="h-6 px-2 text-xs text-destructive">
                  Clear All
                </Button>
              </div>
              <div className="flex flex-wrap gap-1.5 max-h-20 overflow-y-auto">
                {annotations.map((annotation) => (
                  <Badge 
                    key={annotation.id}
                    variant="outline"
                    className="cursor-pointer hover:bg-muted flex items-center gap-1 pr-1"
                    onClick={() => seekTo(annotation.timestamp)}
                  >
                    <div className="w-2 h-2 rounded-full" style={{ backgroundColor: annotation.color }} />
                    <span className="text-xs">{formatTime(annotation.timestamp)}</span>
                    <button 
                      onClick={(e) => { e.stopPropagation(); deleteAnnotation(annotation.id); }}
                      className="ml-0.5 hover:text-destructive"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Tools Panel - Desktop Sidebar / Mobile Bottom Sheet */}
        <div className="lg:w-72 lg:border-l lg:bg-card flex-shrink-0 overflow-x-hidden">
          {/* Mobile Toggle */}
          <button
            className="lg:hidden w-full p-3 border-t bg-card flex items-center justify-between"
            onClick={() => setShowToolsPanel(!showToolsPanel)}
            data-testid="toggle-tools-btn"
          >
            <span className="font-medium text-sm">Tools & Settings</span>
            {showToolsPanel ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}
          </button>

          <div className={`${showToolsPanel ? 'block' : 'hidden'} lg:block overflow-y-auto overflow-x-hidden`} style={{ maxHeight: 'calc(100vh - 120px)' }}>
            {/* Quick Instructions */}
            <div className="p-3 border-b bg-primary/5">
              <div className="flex items-start gap-2">
                <Info className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                <div className="text-xs text-muted-foreground">
                  <strong className="text-foreground">Quick Start:</strong> Pause video, select tool, draw on screen. Annotations show for 3 seconds.
                </div>
              </div>
            </div>

            {/* Tool Tabs */}
            <div className="flex border-b">
              {[
                { id: 'draw', label: 'Draw', icon: Pencil },
                { id: 'voice', label: 'Voice', icon: Mic },
                { id: 'trim', label: 'Trim', icon: Scissors }
              ].map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActivePanel(tab.id)}
                  className={`flex-1 py-2.5 text-xs font-medium flex items-center justify-center gap-1.5 border-b-2 transition-colors ${
                    activePanel === tab.id 
                      ? 'border-primary text-primary' 
                      : 'border-transparent text-muted-foreground hover:text-foreground'
                  }`}
                  data-testid={`tab-${tab.id}`}
                >
                  <tab.icon className="w-3.5 h-3.5" />
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Drawing Tools Panel */}
            {activePanel === 'draw' && (
              <div className="p-3 space-y-4">
                {/* Tool Selection */}
                <div>
                  <Label className="text-xs mb-2 block">Select Tool</Label>
                  <div className="grid grid-cols-3 gap-1.5">
                    {Object.entries(TOOLS).map(([key, value]) => {
                      const config = TOOL_CONFIG[value];
                      const Icon = config.icon;
                      return (
                        <Button
                          key={key}
                          variant={activeTool === value ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => setActiveTool(value)}
                          className="flex flex-col h-auto py-2 gap-0.5"
                          title={config.tip}
                          data-testid={`tool-${value}`}
                        >
                          <Icon className="w-4 h-4" />
                          <span className="text-[10px]">{config.label}</span>
                        </Button>
                      );
                    })}
                  </div>
                  {activeTool !== TOOLS.SELECT && (
                    <p className="text-[10px] text-muted-foreground mt-1.5 text-center">
                      {TOOL_CONFIG[activeTool]?.tip}
                    </p>
                  )}
                </div>

                {/* Color Picker */}
                <div>
                  <Label className="text-xs mb-2 block">Color</Label>
                  <div className="flex gap-1.5 flex-wrap">
                    {COLORS.map(color => (
                      <button
                        key={color}
                        className={`w-7 h-7 rounded-full border-2 transition-all ${
                          activeColor === color ? 'border-foreground scale-110 shadow-md' : 'border-transparent hover:scale-105'
                        }`}
                        style={{ backgroundColor: color }}
                        onClick={() => setActiveColor(color)}
                        data-testid={`color-${color.replace('#', '')}`}
                      />
                    ))}
                  </div>
                </div>

                {/* Line Width */}
                <div>
                  <Label className="text-xs mb-2 block">Thickness: {lineWidth}px</Label>
                  <Slider
                    value={[lineWidth]}
                    min={2}
                    max={12}
                    step={1}
                    onValueChange={([value]) => setLineWidth(value)}
                    data-testid="line-width-slider"
                  />
                </div>

                {/* Text Input (when text tool active) */}
                {activeTool === TOOLS.TEXT && (
                  <div className="p-2 bg-muted/50 rounded-lg space-y-2">
                    {textPosition ? (
                      <>
                        <Input
                          placeholder="Enter text..."
                          value={textInput}
                          onChange={(e) => setTextInput(e.target.value)}
                          onKeyDown={(e) => e.key === 'Enter' && handleAddText()}
                          autoFocus
                          className="h-8 text-sm"
                          data-testid="text-input"
                        />
                        <Button size="sm" onClick={handleAddText} className="w-full h-8">
                          Add Text
                        </Button>
                      </>
                    ) : (
                      <p className="text-xs text-muted-foreground text-center py-2">
                        Tap on the video where you want text
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Voice Panel */}
            {activePanel === 'voice' && (
              <div className="p-3 space-y-4">
                <div className="text-xs text-muted-foreground">
                  Record your voice while the video plays. Your commentary will play in sync with the video when the student watches it.
                </div>
                
                {!isRecording ? (
                  <Button 
                    onClick={startRecording} 
                    variant="outline" 
                    className="w-full"
                    data-testid="start-recording-btn"
                  >
                    <Mic className="w-4 h-4 mr-2" />
                    Start Recording
                  </Button>
                ) : (
                  <Button 
                    onClick={stopRecording} 
                    variant="destructive" 
                    className="w-full animate-pulse"
                    data-testid="stop-recording-btn"
                  >
                    <MicOff className="w-4 h-4 mr-2" />
                    Stop Recording
                  </Button>
                )}
                
                {voiceOverUrl && (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 p-2 bg-green-500/10 text-green-600 rounded text-xs">
                      <div className="w-2 h-2 bg-green-500 rounded-full" />
                      Voice-over recorded{recordingStartTime > 0 ? ` (starts at ${formatTime(recordingStartTime)})` : ''}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Press Play to preview with your voice-over synced to the video.
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Trim Panel */}
            {activePanel === 'trim' && (
              <div className="p-3 space-y-4">
                <div className="text-xs text-muted-foreground">
                  Trim the video to focus on specific moments. Drag the green (start) and red (end) handles.
                </div>
                
                <Button
                  variant={showTrimControls ? 'default' : 'outline'}
                  onClick={() => setShowTrimControls(!showTrimControls)}
                  className="w-full"
                  data-testid="toggle-trim-btn"
                >
                  <Scissors className="w-4 h-4 mr-2" />
                  {showTrimControls ? 'Hide Trim Controls' : 'Enable Trimming'}
                </Button>
                
                {showTrimControls && (
                  <div className="space-y-2 text-xs">
                    <div className="flex justify-between">
                      <span>Start: <span className="font-mono">{formatTime(trimStart)}</span></span>
                      <span>End: <span className="font-mono">{formatTime(trimEnd)}</span></span>
                    </div>
                    <div className="flex justify-between text-muted-foreground">
                      <span>Duration: {formatTime(trimEnd - trimStart)}</span>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Publish Confirmation Modal */}
      {showPublishConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6 shadow-xl">
            <h3 className="font-display font-bold text-lg uppercase mb-3">
              Ready to Publish?
            </h3>
            <p className="text-muted-foreground mb-6">
              Are you sure you want to publish this critique? Once published, your feedback will be sent to the student and they'll be notified that their video response is ready.
            </p>
            <div className="flex gap-3 justify-end">
              <Button 
                variant="outline" 
                onClick={() => setShowPublishConfirm(false)}
                disabled={publishing}
              >
                Cancel
              </Button>
              <Button 
                onClick={() => {
                  setShowPublishConfirm(false);
                  publishVideo();
                }}
                disabled={publishing}
                className="bg-primary"
              >
                {publishing ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    Publishing...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Yes, Publish
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Preview Modal - Shows what student will see */}
      {showPreview && (
        <div className="fixed inset-0 bg-black z-50 flex flex-col">
          <div className="bg-background border-b p-4 flex items-center justify-between">
            <div>
              <h3 className="font-display font-bold text-lg uppercase">Preview Mode</h3>
              <p className="text-sm text-muted-foreground">This is what the student will see</p>
            </div>
            <Button variant="outline" onClick={() => setShowPreview(false)}>
              <X className="w-4 h-4 mr-2" />
              Close Preview
            </Button>
          </div>
          
          <div className="flex-1 flex items-center justify-center p-4">
            <div className="w-full max-w-5xl">
              <div className="relative bg-black rounded-lg overflow-hidden" style={{ aspectRatio: '16/9' }}>
                <video
                  src={project?.source_video_url}
                  className="w-full h-full object-contain"
                  controls
                  autoPlay={false}
                />
                {/* Canvas overlay for annotations - read only */}
                <canvas
                  ref={canvasRef}
                  className="absolute inset-0 w-full h-full pointer-events-none"
                  style={{ mixBlendMode: 'normal' }}
                />
              </div>
              
              <div className="mt-4 bg-muted/50 rounded-lg p-4">
                <h4 className="font-semibold mb-2">Preview Summary</h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div>
                    <span className="text-muted-foreground">Annotations:</span>
                    <span className="ml-2 font-medium">{annotations.length}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Voice-over:</span>
                    <span className="ml-2 font-medium">{voiceOverUrl ? 'Yes' : 'No'}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Trimmed:</span>
                    <span className="ml-2 font-medium">{showTrimControls ? 'Yes' : 'No'}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Duration:</span>
                    <span className="ml-2 font-medium">{formatTime(duration)}</span>
                  </div>
                </div>
                {annotations.length === 0 && !voiceOverUrl && (
                  <p className="text-amber-600 mt-3 text-sm">
                    ⚠️ You haven't added any annotations or voice-over yet. Consider adding feedback before publishing.
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VideoStudioPage;
